import type { Profile, ProfileComponent, ProfileRoleType } from '@nx/domain';
import { normalizeProfileOptionalText } from '@nx/domain';

export interface ProfileViewModel {
  id: string;
  name: string;
  slug: string;
  roleType: ProfileRoleType;
  role: string;
  bio: string;
  initials: string;
  accent: string;
  location: string | null;
  tags: string[];
  status: Profile['status'];
  themeId: string;
  viewCount: number;
  avatarUrl: string | null;
  backgroundImageUrl: string | null;
  backgroundColor: string | null;
  components: ProfileComponentViewModel[];
}

export interface ProfileComponentViewModel {
  id: string;
  type: string;
  platform?: string;
  label: string;
  handle?: string;
  note?: string;
  body?: string;
  caption?: string;
  url?: string;
  videoId?: string;
  imageUrl?: string;
  visible: boolean;
  clickCount: number;
}

function initialsFromTitle(title: string): string {
  return title
    .split(/\s+/)
    .map((s) => s[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

function mapComponentType(type: ProfileComponent['type']): string {
  const map: Record<ProfileComponent['type'], string> = {
    social_link: 'social',
    custom_link: 'link',
    text_block: 'text',
    image_block: 'image',
    youtube: 'youtube',
    instagram: 'instagram',
    twitter: 'social',
  };
  return map[type] ?? 'link';
}

export function toProfileViewModel(
  profile: Profile,
  components: ProfileComponent[],
): ProfileViewModel {
  return {
    id: profile.id,
    name: profile.title,
    slug: profile.slug,
    roleType: profile.roleType,
    role: normalizeProfileOptionalText(profile.tagline) ?? '',
    bio: profile.description ?? '',
    initials: initialsFromTitle(profile.title),
    accent: profile.themeConfig.primaryColor,
    location: normalizeProfileOptionalText(profile.location),
    tags: profile.tags,
    status: profile.status,
    themeId: profile.themeConfig.templateId,
    viewCount: profile.viewCount,
    avatarUrl: profile.avatarUrl,
    backgroundImageUrl: profile.themeConfig.backgroundImageUrl,
    backgroundColor: profile.themeConfig.backgroundColor,
    components: components.map((c) => ({
      id: c.id,
      type: mapComponentType(c.type),
      platform: c.config.platform,
      label: c.config.label ?? '',
      handle: c.config.handle,
      note: c.config.note,
      body: c.config.content,
      caption: c.config.caption,
      url: c.config.url,
      videoId: c.config.embedId,
      imageUrl: c.config.imageUrl,
      visible: c.visible,
      clickCount: c.clickCount,
    })),
  };
}
